import dayjs from "dayjs";
import { notFound } from "next/navigation";
import { unparse } from "papaparse";
import React, { useEffect, useState } from "react";
import { Button as RAButton } from "react-aria-components";
import {
  Button,
  Heading,
  LinkButton,
  Spinner,
  Text,
  DatePicker,
} from "ui-components";

import { OrganizationTabsEnum } from "@/src/@types/Job";
import { HiringPipeline } from "@/src/app/[locale]/(private)/profile/[tab]/components/hiring-pipeline/HiringPipeline";
import { makeToast } from "@/src/components/Toast/Toast";
import {
  OrganizationJobClicksByCountryResponse,
  OrganizationJobPerformanceDashboardResponse,
  useOrganizationCheckHiringDataAvailability,
  useOrganizationGetOrganizationDashboard,
  useOrganizationOrganizationJobClicksByCountry,
  useOrganizationOrganizationJobPerformanceDashboard,
  useOrganizationOrganizationJobPerformanceReport,
} from "@/src/hooks/api/generated";

import { ClicksByCountryInfo } from "./ClicksByCountryInfo";
import { JobsDataTable } from "./JobsDataTable";
import { OrganizationExclusiveWebinar } from "./OrganizationExclusiveWebinar";
import { OrganizationInfomartion } from "./OrganizationInfomartion";

const pageSize = 10;
export function MyOrganizationProfile({
  organizationId,
}: {
  organizationId?: number;
}): JSX.Element {
  const [activeTab, setActiveTab] = useState(1);
  const [filterDates, setFilterDates] = useState({
    start: dayjs().subtract(30, "days"),
    end: dayjs(),
  });

  const [jobStats, setJobStats] =
    useState<OrganizationJobPerformanceDashboardResponse>();

  const [orgnisationJobClicksByCountry, setOrgnisationJobClicksByCountry] =
    useState<OrganizationJobClicksByCountryResponse>();

  const params = organizationId
    ? { organizationId: String(organizationId) }
    : undefined;
  const { data: organizationData, isLoading: isLoadingOrganizationData } =
    useOrganizationGetOrganizationDashboard(params);

  const { mutate: getJobPerformanceData, status: jobPerformanceDataStatus } =
    useOrganizationOrganizationJobPerformanceDashboard();

  const {
    mutate: getOrganizationJobClicksByCountry,
    status: organizationJobClicksByCountryStatus,
  } = useOrganizationOrganizationJobClicksByCountry();

  const { mutate: downloadJobReport, status: downloadJobReportStatus } =
    useOrganizationOrganizationJobPerformanceReport();

  const { data: checkHiringDataAvailability } =
    useOrganizationCheckHiringDataAvailability();

  const [page, setPage] = useState(1);

  useEffect(() => {
    getOrganizationJobClicksByCountry(
      {
        data: {
          startDate: dayjs(filterDates.start).format(),
          endDate: dayjs(filterDates.end).format(),
          organizationId,
        },
      },
      {
        onSuccess: (data) => {
          setOrgnisationJobClicksByCountry(data);
        },
        onError: (_error) => {
          makeToast({
            title: "Something went wrong.",
            intent: "danger",
          });
        },
      }
    );
    getJobPerformanceData(
      {
        data: {
          startDate: dayjs(filterDates.start).format(),
          endDate: dayjs(filterDates.end).format(),
          page,
          pageSize,
          organizationId,
        },
      },
      {
        onSuccess: (data) => {
          setJobStats(data);
        },
        onError: (_error) => {
          makeToast({
            title: "Something went wrong.",
            intent: "danger",
          });
        },
      }
    );
  }, [filterDates, page, getJobPerformanceData, organizationId]);

  const downloadReport = (): void => {
    downloadJobReport(
      {
        data: {
          startDate: dayjs(filterDates.start).format(),
          endDate: dayjs(filterDates.end).format(),
          organizationId,
        },
      },
      {
        onSuccess: (data) => {
          const csv = unparse(
            data.map((item) => {
              const { id, slug, title, views, clicks, country } = item;
              return {
                "Job ID": id,
                "Job Title": title,
                views,
                "Clicks on Apply": clicks,
                Country: country,
                "Job URL": `${process.env.APP_URL}/jobs/${slug}`,
              };
            })
          );
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          const link = document.createElement("a");
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", "job-report.csv");
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        onError: (_error) => {
          makeToast({
            title: "Something went wrong.",
            intent: "danger",
          });
        },
      }
    );
  };

  if (isLoadingOrganizationData) {
    return (
      <div className="mt-10 w-full flex justify-center">
        <Spinner intent="brand-primary" size="lg" />
      </div>
    );
  }

  if (!isLoadingOrganizationData && !organizationData) {
    return notFound();
  }

  return (
    <div className="w-full flex flex-col md:flex-row">
      <OrganizationInfomartion organizationId={organizationId} />
      <div className="w-full md:w-3/5 px-5 flex flex-col gap-y-5">
        <Heading variant="h2">Your Job Performance Dashboard</Heading>

        <div>
          <Text variant="small">Filter by</Text>
          <div className="flex items-start gap-x-1.5 justify-start ">
            <DatePicker
              selectDate={filterDates.start.format("YYYY-MM-DD")}
              minDate={dayjs()
                .subtract(2, "year")
                .startOf("year")
                .format("YYYY-MM-DD")}
              isDisabled={
                jobPerformanceDataStatus === "pending" ||
                organizationJobClicksByCountryStatus === "pending"
              }
              maxDate={filterDates.end.format("YYYY-MM-DD")}
              onSelectNewDate={(newData) => {
                setFilterDates({
                  ...filterDates,
                  start: dayjs(newData),
                });
              }}
            />
            <Text className="m-2.5">to</Text>
            <DatePicker
              selectDate={filterDates.end.format("YYYY-MM-DD")}
              minDate={filterDates.start.format("YYYY-MM-DD")}
              maxDate={dayjs().subtract(1, "day").format("YYYY-MM-DD")}
              isDisabled={
                jobPerformanceDataStatus === "pending" ||
                organizationJobClicksByCountryStatus === "pending"
              }
              onSelectNewDate={(newData) => {
                setFilterDates({
                  ...filterDates,
                  end: dayjs(newData),
                });
              }}
            />

            <div className="ml-5 mt-2.5">
              {(jobPerformanceDataStatus === "pending" ||
                organizationJobClicksByCountryStatus === "pending") && (
                <Spinner size="md" intent="brand-primary" />
              )}
            </div>
          </div>

          {dayjs(filterDates.end).isBefore(dayjs(filterDates.start)) && (
            <Text intent="danger">
              From date cannot be later than the End date.
            </Text>
          )}
        </div>
        {jobPerformanceDataStatus === "pending" && !jobStats && (
          <div className="w-full flex justify-center my-10">
            <Spinner size="lg" intent="brand-primary" />
          </div>
        )}
        {jobStats && (
          <>
            <div className="flex flex-wrap md:flex-nowrap w-full gap-5">
              <StatisticCard>
                <Heading variant="h2">{jobStats?.activeJobs}</Heading>
                <Text variant="large">Active Jobs</Text>
              </StatisticCard>
              <StatisticCard>
                <Heading variant="h2">{jobStats?.totalViews}</Heading>
                <Text variant="large">Jobs Views</Text>
              </StatisticCard>
              <StatisticCard>
                <Heading variant="h2">{jobStats?.totalClicks}</Heading>
                <Text variant="large">Clicks on Apply</Text>
              </StatisticCard>
            </div>
            <div className="w-full flex">
              <RAButton
                className={`w-full py-5 border-b outline-none border-slate-400 ${
                  activeTab === 1 ? "!border-primary" : "bg-transparent"
                }`}
                onPress={() => setActiveTab(1)}
              >
                <Text
                  className={`font-normal ${
                    activeTab === 1 ? "!font-bold" : ""
                  }`}
                >
                  Jobs
                </Text>
              </RAButton>
              <RAButton
                className={`w-full py-5 border-b outline-none border-slate-400 ${
                  activeTab === 2 ? "!border-primary" : "bg-transparent"
                }`}
                onPress={() => setActiveTab(2)}
              >
                <Text
                  className={`font-normal ${
                    activeTab === 2 ? "!font-bold" : ""
                  }`}
                >
                  Clicks by Country
                </Text>
              </RAButton>
              {checkHiringDataAvailability &&
                checkHiringDataAvailability.hasHiringData && (
                  <RAButton
                    className={`w-full py-5 font-normal border-b outline-none border-slate-400 ${
                      activeTab === 3 ? "!border-primary" : "bg-transparent"
                    }`}
                    onPress={() => setActiveTab(3)}
                  >
                    <Text
                      className={`font-normal ${
                        activeTab === 3 ? "!font-bold" : ""
                      }`}
                    >
                      Hiring Pipeline
                    </Text>
                  </RAButton>
                )}
            </div>
            <div>
              {activeTab === 1 && (
                <JobsDataTable
                  data={jobStats.jobsData}
                  page={page}
                  pageSize={pageSize}
                  setPage={setPage}
                />
              )}
              {activeTab === 2 && jobStats?.clicksByCountry && (
                <ClicksByCountryInfo
                  organisationJobsClickData={orgnisationJobClicksByCountry}
                />
              )}
              {checkHiringDataAvailability &&
                checkHiringDataAvailability.hasHiringData === true &&
                activeTab === 3 && <HiringPipeline />}
            </div>
          </>
        )}
        <div className="flex flex-col gap-y-10 mt-5">
          {activeTab === 1 && !!jobStats?.jobsData.total && (
            <Button
              isDisabled={downloadJobReportStatus === "pending"}
              isLoading={downloadJobReportStatus === "pending"}
              className="mx-auto"
              onClick={() => downloadReport()}
            >
              Download job report
            </Button>
          )}
          {!organizationId && (
            <div className="flex flex-col gap-y-5">
              <Heading variant="h2">Invite colleagues</Heading>
              <Text>Invite your colleagues to join myGwork</Text>
              <LinkButton
                href={`/organizations/${organizationData?.organization.slug}/${OrganizationTabsEnum.INVITE_EMPLOYEES}`}
              >
                Invite colleagues
              </LinkButton>
            </div>
          )}
        </div>
      </div>
      {!organizationId && <OrganizationExclusiveWebinar />}
    </div>
  );
}

function StatisticCard({ children }: { children: JSX.Element[] }): JSX.Element {
  return (
    <div className="w-full flex flex-col bg-primary bg-opacity-10 p-10 items-center justify-center text-center">
      {children}
    </div>
  );
}
